import {useDispatch, useSelector} from 'react-redux';
import {assessmentsConfig, externalConfigs, saveExternalConfigs} from '../../../../store/reducers/externalConfig';
import AgyleToggleButtons from '../../../../components/ui/buttons/agyleToggleButtons';
import React, {useEffect, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import {ASSESSMENT_MANDATORY_CONFIG} from '../../../../util/patientContextUtil';
import _ from 'lodash';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import SaveIcon from '@mui/icons-material/Save';
import Typography from '@mui/material/Typography';
import {Divider} from '@mui/material';
import AgyleSwitch from '../../../../components/ui/switch/agyleSwitch';
import {DEFAULT_ASSESSMENTS_VALUE} from '../../aglyeConfig/configDefaultValues';

const useStyles = makeStyles((theme) => ({
    toggleRoot: {
        color: theme.palette.mode === 'dark' ? '#222222' : '#f3f3f4',
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#eff1f3',
        display: 'inline-block',
        width: '100%',
        padding: '5px !important',
    },
    toggleLabel: {
        color: theme.palette.mode === 'dark' ? '#ACAFB3' : '#222222',
        height: '25px !important',
        fontSize: 12,
        '&.icon': {
            fontSize: 10,
        },
        '&.Mui-disabled': {
            border: 'none',
        },
    },
    toggleSelected: {
        color: theme.palette.mode === 'dark' ? '#222222 !important' : '#ffffff !important',
        fontWeight: 700,
        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff !important' : '#626466 !important',
    },
    button: {
        width: '100%',
        marginBottom: 15,
        display: 'flex',
        justifyContent: 'flex-end',
    },
}));

const OPTIONS = [
    {
        value: ASSESSMENT_MANDATORY_CONFIG.DONT_SHOW_BORDERLINE,
        label: 'Do not show borderline',
        description: 'Patient context buttons do not show red border on mandatory assessment form',
    },
    {
        value: ASSESSMENT_MANDATORY_CONFIG.ALWAYS_SHOW_BORDERLINE,
        label: 'Always show borderline',
        description: 'Patient context buttons always show red border on uncompleted mandatory assessment form',
    },
    {
        value: ASSESSMENT_MANDATORY_CONFIG.SHOW_BORDERLINE_ON_DISCHARGE,
        label: 'Show borderline on discharge only',
        description: 'Patient context buttons show red border on uncompleted mandatory assessment form only when trying to discharge',
    },
];

const OPTIONS2 = [
    {
        value: ASSESSMENT_MANDATORY_CONFIG.DONT_DISPLAY_MODAL_ON_DISCHARGE,
        label: 'Do not display modal',
    },
    {
        value: ASSESSMENT_MANDATORY_CONFIG.DISPLAY_MODAL_ON_DISCHARGE,
        label: 'Display warning modal on discharge with form to be completed',
    },
];

const AssessmentMandatoryConfiguration = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const cfgs = useSelector(externalConfigs);
    const assCfg = useSelector(assessmentsConfig);

    const [externalConfig, setExternalConfig] = useState(null);
    const [formMandatory, setFormMandatory] = useState(null);

    useEffect(() => {
        const find = _.find(cfgs, cfg => cfg.configType === 'assessments');
        if (find?.value) {
            setExternalConfig(find);
            setFormMandatory(find?.value?.mandatory || DEFAULT_ASSESSMENTS_VALUE.mandatory);
        }
    }, [cfgs]);

    const handleSave = () => {
        const clone = _.cloneDeep(assCfg);
        clone.mandatory = formMandatory;

        const objToSave = {
            __id: externalConfig?.__id || null,
            description: 'Agyle Assessment list',
            configType: 'assessments',
            active: true,
            value: clone,
        };
        dispatch(saveExternalConfigs(objToSave));
    };

    return (
        <>
            <div className={classes.button}>
                <AgyleButton
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon/>}
                    onClick={handleSave}
                >
                    {t('Save configuration')}
                </AgyleButton>
            </div>

            <Typography variant="subtitle2" sx={{marginBottom: '10px'}}>
                {t('Enables mandatory assessment at discharge')}
            </Typography>

            <AgyleSwitch
                label={t('Enabled')}
                value={formMandatory?.enabled}
                onChange={() => setFormMandatory(prev => ({...prev, enabled: !prev.enabled}))}
            />

            <Divider sx={{m: 1}}/>

            <Typography variant="subtitle2" sx={{marginBottom: '10px'}}>
                {t('Borderline assessment mandatory configuration')}
            </Typography>

            <AgyleToggleButtons
                classNameRoot={classes.toggleRoot}
                classNameLabel={classes.toggleLabel}
                classNameSelected={classes.toggleSelected}
                onChange={(e) => {
                    if (e) {
                        setFormMandatory(prev => ({
                            ...prev,
                            showBorderline: e,
                        }));
                    }
                }}
                value={formMandatory?.showBorderline}
                size="small"
                options={OPTIONS}
                minWidth={100}
                fullWidth
            />

            <Typography variant="body2" style={{marginTop: 10}} fontWeight={600}>
                {formMandatory?.showBorderline && OPTIONS.find(option => option.value === formMandatory?.showBorderline)?.description}
            </Typography>

            <Divider sx={{m: 1}}/>

            <Typography variant="subtitle2" sx={{marginBottom: '10px'}}>
                {t('Modal display configuration')}
            </Typography>

            <AgyleToggleButtons
                classNameRoot={classes.toggleRoot}
                classNameLabel={classes.toggleLabel}
                classNameSelected={classes.toggleSelected}
                onChange={(e) => {
                    if (e) {
                        setFormMandatory(prev => ({
                            ...prev,
                            modalDisplay: e,
                        }));
                    }
                }}
                value={formMandatory?.modalDisplay}
                size="small"
                options={OPTIONS2}
                minWidth={100}
                fullWidth
            />
        </>
    );
};

export default AssessmentMandatoryConfiguration;